import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { seekSupabase } from '@/integrations/supabase/seekClient';
import { useSeekAuth } from '@/contexts/SeekAuthContext';
import { useToast } from '@/hooks/use-toast';
import type { Json } from '@/integrations/supabase/types';
import { resolveSeekMatchScoreState, type SeekMatchScoreDisplay } from '@/lib/seekMatchScoreState';

function isString(value: string | null): value is string {
  return typeof value === 'string';
}

export function useSeekApplications() {
  const { user } = useSeekAuth();
  const uid = user?.id;
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const query = useQuery({
    queryKey: ['seek-applications', uid],
    queryFn: async () => {
      const { data, error } = await seekSupabase
        .from('seek_applications')
        .select('*, seek_job_catalog(*)')
        .eq('candidate_id', uid!)
        .order('created_at', { ascending: false });
      if (error) throw error;

      // Fetch match scores for these applications (discovery score)
      const jobCatalogIds = (data ?? [])
        .map((application) => application.seek_job_catalog_id)
        .filter(isString);

      let profileUpdatedAt: string | null = null;
      const { data: profile } = await seekSupabase
        .from('candidate_profiles')
        .select('updated_at')
        .eq('candidate_id', uid!)
        .maybeSingle();
      profileUpdatedAt = profile?.updated_at ?? null;

      let matchScoreMap: Record<string, SeekMatchScoreDisplay> = {};
      if (jobCatalogIds.length > 0) {
        const { data: scores } = await seekSupabase
          .from('job_match_scores')
          .select('seek_job_catalog_id, match_score, updated_at')
          .eq('candidate_id', uid!)
          .in('seek_job_catalog_id', jobCatalogIds);
        if (scores) {
          matchScoreMap = Object.fromEntries(
            scores.map((score) => {
              const display = resolveSeekMatchScoreState({
                score: score.match_score,
                computedAt: score.updated_at,
                profileUpdatedAt,
              });
              return [score.seek_job_catalog_id, display];
            })
          );
        }
      }

      // Fetch applied AI CV scores (authoritative employer-side score)
      const hireApplicationIds = (data ?? [])
        .map((application) => application.hire_application_id)
        .filter(isString);

      let cvScoreMap: Record<string, { total_score: number | null; breakdown_json: Json | null; explanation_text: string | null }> = {};
      if (hireApplicationIds.length > 0) {
        const { data: cvScores } = await seekSupabase
          .from('cv_scores')
          .select('application_id, total_score, breakdown_json, explanation_text')
          .in('application_id', hireApplicationIds);

        if (cvScores) {
          cvScoreMap = Object.fromEntries(
            cvScores.map((score) => [score.application_id, {
              total_score: score.total_score ?? null,
              breakdown_json: score.breakdown_json ?? null,
              explanation_text: score.explanation_text ?? null,
            }])
          );
        }
      }

      return (data ?? []).map((application) => {
        const cvScore = application.hire_application_id
          ? cvScoreMap[application.hire_application_id]
          : undefined;
        return {
          ...application,
          match_score: matchScoreMap[application.seek_job_catalog_id]?.score ?? null,
          match_score_state: matchScoreMap[application.seek_job_catalog_id]?.state ?? 'missing',
          applied_cv_score: cvScore?.total_score ?? null,
          applied_cv_breakdown: cvScore?.breakdown_json ?? null,
          applied_cv_explanation: cvScore?.explanation_text ?? null,
        };
      });
    },
    enabled: !!uid,
  });

  const withdrawMutation = useMutation({
    mutationFn: async (applicationId: string) => {
      const { data, error } = await seekSupabase.rpc('withdraw_application', {
        p_seek_application_id: applicationId,
      });
      if (error) throw error;
      if (data === false) throw new Error('Application not found or already withdrawn');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seek-applications'] });
      queryClient.invalidateQueries({ queryKey: ['seek-jobs'] });
      toast({ title: 'Application withdrawn', description: 'Your application has been withdrawn successfully.' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to withdraw application. Please try again.', variant: 'destructive' });
    },
  });

  return {
    applications: query.data ?? [],
    isLoading: query.isLoading,
    withdrawApplication: withdrawMutation,
  };
}
